import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { Button, buttonVariants } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { cn } from "../../lib/cn"

type UserFormData = {
  id?: number
  email: string
  password: string
  password_confirmation: string
}

type UserFormProps = {
  initialValues: UserFormData
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

export function UserForm({ initialValues, submitUrl, submitLabel, method, errors }: UserFormProps) {
  const form = useForm({
    user: {
      email: initialValues.email ?? "",
      password: initialValues.password ?? "",
      password_confirmation: initialValues.password_confirmation ?? ""
    }
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            value={form.data.user.email}
            onChange={(e) => form.setData("user", { ...form.data.user, email: e.target.value })}
          />
          <ErrorText value={errors?.email} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Senha {method === "patch" ? "(opcional)" : ""}</Label>
          <Input
            id="password"
            type="password"
            value={form.data.user.password}
            onChange={(e) => form.setData("user", { ...form.data.user, password: e.target.value })}
          />
          <ErrorText value={errors?.password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password_confirmation">Confirmação de senha</Label>
          <Input
            id="password_confirmation"
            type="password"
            value={form.data.user.password_confirmation}
            onChange={(e) => form.setData("user", { ...form.data.user, password_confirmation: e.target.value })}
          />
          <ErrorText value={errors?.password_confirmation} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Link href="/usuarios" className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}>
          Cancelar
        </Link>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
